import {useState, useRef, useEffect, useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import { useSelector } from 'react-redux';
// import useOrderNotifications from '../hooks/useOrderNotifications';
import { useOrderFlow } from '../app/providers/OrderFlowProvider';
import { useNavigate } from 'react-router-dom';
import { formatDatetimeByLocale } from '../helpers/normalizers';

const OrdersNotification = () => {
    const {i18n, t} = useTranslation();

    const navigate = useNavigate();

    // // Get notifications from useOrderNotifications Hook
    // const { notificationOrders } = useOrderNotifications();

    const notificationOrders = useSelector((state: RootState) => state.notifications.orders);
    const excludedOrdersData = useSelector((state: RootState) => state.notifications.excludedOrders);

    const filteredOrders = useMemo(() => {
        return (
        (notificationOrders || []).filter(order => !Object.keys(excludedOrdersData || {}).includes(order?.id?.toString()))
    )}, [notificationOrders, excludedOrdersData]);

    // const prevCountRef = useRef(0);
    // useEffect(() => {
    //     if (
    //         notificationOrders.length > 0 &&
    //         notificationOrders.length > prevCountRef.current
    //     ) {
    //         audioRef.current?.play().catch(() => {});
    //     }
    //
    //     prevCountRef.current = notificationOrders.length;
    // }, [notificationOrders]);

    const {
        openOrderByOrderDetails,
    } = useOrderFlow();

    const [isOpen, setIsOpen] = useState(false);
    const bellRef = useRef<HTMLDivElement>(null);

    const toggleDropdown = () => setIsOpen(!isOpen);


    const ensureMainRoute = useCallback(() => {
        if (window.location.pathname !== "/") {
            const currSP_str = new URLSearchParams(window.location.search).toString();
            navigate(`/${currSP_str ? "?" + currSP_str : ""}`, { replace: true });
            return false;
        }
        return true;
    }, []);

    // const openOrderFromNotification = ({ orderDetails }) => {
    const openOrderIfOnAnotherRoute = ({ orderDetails }) => {
        const isOnMain = ensureMainRoute();
        if (!isOnMain) {
            setTimeout(() => {
                openOrderByOrderDetails({ orderDetails });
            }, 0);
            return;
        }

        openOrderByOrderDetails({ orderDetails });
    };

    const handleSelectOrder = async (orderDetails) => {
        if (!orderDetails) return;
        openOrderIfOnAnotherRoute({ orderDetails });
        setIsOpen(false);
    };


    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (bellRef.current && !bellRef.current.contains(event.target as Element)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div
            ref={bellRef}
            className="position-relative me-4"
        >
            <button
                className={`btn btn-link position-relative p-0 ${filteredOrders.length > 0 ? 'bell-shake' : ''}`}
                onClick={toggleDropdown}
            >
                {/* Larger SVG Bell Icon */}
                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="currentColor" viewBox="0 0 16 16"><path d="M8 16a2 2 0 0 0 1.985-1.75H6.015A2 2 0 0 0 8 16zm.104-14.998a1 1 0 0 0-.208.004A1.5 1.5 0 0 0 8 2c0 .22-.07.44-.208.622C5.825 4.94 5 7.188 5 10v1l-.854 1.707A.5.5 0 0 0 4.5 13h7a.5.5 0 0 0 .354-.854L11 11v-1c0-2.812-.825-5.06-2.792-7.378zM14 10c0 3-1 5-6 5s-6-2-6-5a6.993 6.993 0 0 1 1.528-4.182A4.992 4.992 0 0 1 8 1a4.992 4.992 0 0 1 4.472 4.818A6.993 6.993 0 0 1 14 10z"/></svg>

                {filteredOrders.length > 0 && (
                    <span className="badge bg-danger d-flex justify-content-center align-items-center p-0 orders-notification-bell-badge position-absolute top-0 start-100 translate-middle rounded-circle">
                        {filteredOrders.length}
                    </span>
                )}
            </button>

            {isOpen && (
                <div className="dropdown-menu orders-notification-dropdown show p-2 shadow-lg custom-dropdown">
                    <h3 className="dropdown-header border-bottom fs-5 mb-3 pb-3">
                        {t('deliveryAndPickupOrders')}
                    </h3>

                    {filteredOrders.length === 0 ? (
                        <div className="dropdown-item text-muted my-4 py-3 text-center">
                            {t('noOrders')}
                        </div>
                    ) : (
                        filteredOrders.map((order) => {
                            const tipAmount = "tips" in order && Array.isArray(order?.tips)
                                ? order.tips.reduce((sum, tipItem) => {
                                    return sum + (Number(tipItem?.amount) || 0);
                                }, 0)
                                : 0;

                            return (
                                <div
                                    key={order.id}
                                    className="dropdown-item notification-order_details_btn"
                                    data-id={<>order.id</>}
                                    onClick={() => handleSelectOrder(order)}
                                >
                                    <div className="d-flex justify-content-between align-items-center">
                                        <strong>
                                            {t('orderId')}: {order.id}
                                        </strong>

                                        {formatDatetimeByLocale(order.created_at_timezone, i18n) !== 'Invalid Date' && (
                                            <span className="time text-muted small">
                                                {formatDatetimeByLocale(order.created_at_timezone, i18n)}
                                            </span>
                                        )}
                                    </div>

                                    <div className="message mt-1">
                                        <ul className="list-unstyled m-0 p-0">
                                            <li>
                                                {t('totalPrice')} : {order.cost} €
                                                {tipAmount > 0 && (
                                                    <small className="text-muted ms-1">
                                                        (+{tipAmount.toFixed(2)} {t("tip")})
                                                    </small>
                                                )}
                                            </li>
                                            <li>
                                                {t('orderType')} : {order.order_type}
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            )}
        </div>
    );
};

export default OrdersNotification;
